import {
  ClientIdContext,
  PersonIdContext,
  mapAddOnsToOptions,
  mapDataWithIdAndNameToOptions,
} from 'bk-web-app';
import { defaultDataIdFromObject, gql } from '@apollo/client';
import { Mutation, Query } from '@apollo/client/react/components';
import { getFormElementValues, toastError, toastSuccess } from 'bench-internal-theme';
import React from 'react';
import ApplyConfirmationModal from './components/apply-confirmation-modal/ApplyConfirmationModal';
import { openModal } from 'modal-core-components';
import {
  apolloV2Client,
  BULK_EDIT_DATA_ENTRIES,
  GET_BALANCES_DATA,
  GET_BULK_EDIT_FORM_DATA,
} from './queries';
import BulkEditForm from './BulkEditForm';
import { SelectedDataEntriesConsumer } from './SelectedDataEntriesContext';
import type { DataEntry, DateRange, GeneralLedgerFilters, Client } from './types';

import {
  TAG_1099_TRANSACTION,
  UNTAG_1099_JOURNAL_ENTRIES,
} from './components/report-1099-form/graphql/mutations';
import { showServerError, graphqlClient } from 'bk-web-app';
import { getJournalEntries1099RefetchQuery } from 'bk-web-app/transaction-manager/components/transactions-1099-container/graphql';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';

let nextNoteId = 1;
const getNoteId = () => `bulk-edit-form-provisional-note-id-${nextNoteId++}`;

type UpdateDataEntriesInput = {
  clientId: string;
  dataEntryIds: Array<string>;
  addOnId?: string | null;
  internalNoteBody?: string | null;
  ledgerAccountId?: string | null;
  vendorName?: string | null;
  vendorNamePrefix?: string | null;
  vendorNameSuffix?: string | null;
  contractor?: string | null;
  source?: string | null;
  isTagged1099: boolean;
};

const updateCacheAfterBulkEdit = (
  cache,
  dataEntriesBeingBulkEdited,
  updateDataEntriesInput: Omit<UpdateDataEntriesInput, 'contractor' | 'source'>,
) => {
  dataEntriesBeingBulkEdited.forEach(dataEntryObject => {
    // Adding __typename to ensure dataEntryObjects can saved to the cache successfully
    const dataEntry = {
      ...dataEntryObject,
      __typename: 'DataEntry',
    };
    if (updateDataEntriesInput.addOnId) {
      cache.writeFragment({
        id: defaultDataIdFromObject(dataEntry.journalEntry),
        fragment: gql`
          fragment bulkEditJournalEntryAddOn on JournalEntry {
            addOn {
              id
            }
            __typename
          }
        `,
        data: {
          addOn: {
            id: updateDataEntriesInput.addOnId,
            __typename: 'AddOn',
          },
          __typename: 'JournalEntry',
        },
      });
    }
    if (updateDataEntriesInput.internalNoteBody) {
      const internalNotes = dataEntry.journalEntry.internalNotes || [];
      cache.writeFragment({
        id: defaultDataIdFromObject(dataEntry.journalEntry),
        fragment: gql`
          fragment bulkEditJournalEntryInternalNotes on JournalEntry {
            internalNotes {
              id
              body
              flagged
            }
            __typename
          }
        `,
        data: {
          internalNotes: [
            ...internalNotes,
            {
              id: getNoteId(),
              body: updateDataEntriesInput.internalNoteBody,
              flagged: false,
              __typename: 'InternalNote',
            },
          ],
          __typename: 'JournalEntry',
        },
      });
    }
    if (updateDataEntriesInput.ledgerAccountId) {
      cache.writeFragment({
        id: defaultDataIdFromObject(dataEntry),
        fragment: gql`
          fragment bulkEditDataEntryLedgerAccount on DataEntry {
            ledgerAccount {
              id
            }
            __typename
          }
        `,
        data: {
          ledgerAccount: {
            id: updateDataEntriesInput.ledgerAccountId,
            __typename: 'LedgerAccount',
          },
          __typename: 'DataEntry',
        },
      });
    }
    if (
      updateDataEntriesInput.vendorName ||
      updateDataEntriesInput.vendorNamePrefix ||
      updateDataEntriesInput.vendorNameSuffix
    ) {
      let updatedVendorName =
        updateDataEntriesInput.vendorName || dataEntry.journalEntry.vendorName;
      if (updateDataEntriesInput.vendorNamePrefix) {
        updatedVendorName = `${updateDataEntriesInput.vendorNamePrefix} ${updatedVendorName}`;
      } else if (updateDataEntriesInput.vendorNameSuffix) {
        updatedVendorName = `${updatedVendorName} ${updateDataEntriesInput.vendorNameSuffix}`;
      }
      cache.writeFragment({
        id: defaultDataIdFromObject(dataEntry.journalEntry),
        fragment: gql`
          fragment bulkEditJournalEntryVendorName on JournalEntry {
            vendorName
            __typename
          }
        `,
        data: {
          vendorName: updatedVendorName,
          __typename: 'JournalEntry',
        },
      });
    }
  });
};

const updateCacheAfter1099Action = (
  dataEntriesBeingBulkEdited,
  updateDataEntriesInput: Pick<UpdateDataEntriesInput, 'contractor' | 'source' | 'isTagged1099'>,
) => {
  dataEntriesBeingBulkEdited.forEach(dataEntryObject => {
    // Adding __typename to ensure dataEntryObjects can saved to the cache successfully
    const dataEntry = {
      ...dataEntryObject,
      __typename: 'DataEntry',
    };
    if (updateDataEntriesInput.isTagged1099) {
      apolloV2Client.writeFragment({
        id: defaultDataIdFromObject(dataEntry.journalEntry),
        fragment: gql`
          fragment bulkEditDataEntryContractor on JournalEntry {
            isTagged1099
            contractorId
            source1099
            __typename
          }
        `,
        data: {
          isTagged1099: updateDataEntriesInput.isTagged1099,
          contractorId: updateDataEntriesInput.contractor,
          source1099: updateDataEntriesInput.source,
          __typename: 'JournalEntry',
        },
      });
    } else if (!updateDataEntriesInput.isTagged1099) {
      apolloV2Client.writeFragment({
        id: defaultDataIdFromObject(dataEntry.journalEntry),
        fragment: gql`
          fragment bulkEditDataEntryContractor on JournalEntry {
            isTagged1099
            __typename
          }
        `,
        data: {
          isTagged1099: updateDataEntriesInput.isTagged1099,
          __typename: 'JournalEntry',
        },
      });
    }
  });
};

const tag1099Transaction = ({
  updateDataEntriesInput,
  dataEntriesBeingBulkEdited,
  clientId,
  variables,
}) => {
  if (window.heap) {
    window.heap.track('Bookkeeper tagged the transaction as 1099', {
      clientId,
      clientAction: 'Bookkeeper tagged the transaction as 1099',
    });
  }
  return graphqlClient
    .mutate({
      mutation: TAG_1099_TRANSACTION,
      variables,
      context: { useV2: true },
      update: () => updateCacheAfter1099Action(dataEntriesBeingBulkEdited, updateDataEntriesInput),
      refetchQueries: getJournalEntries1099RefetchQuery(clientId),
    })
    .then(() => {
      toastSuccess('1099 Transaction Tagged', 'You have successfully tagged a 1099 transaction.', {
        position: 'top-right',
      });
      showBulkEditCompletedToast(dataEntriesBeingBulkEdited.length);
    })
    .catch(error =>
      toastError('An error occurred tagging this transaction as 1099', showServerError(error), {
        position: 'top-right',
      }),
    );
};

const untag1099Transaction = ({
  variables,
  dataEntriesBeingBulkEdited,
  updateDataEntriesInput,
  clientId,
}) => {
  return graphqlClient.mutate({
    mutation: UNTAG_1099_JOURNAL_ENTRIES,
    variables,
    context: { useV2: true },
    update: () => updateCacheAfter1099Action(dataEntriesBeingBulkEdited, updateDataEntriesInput),
    refetchQueries: getJournalEntries1099RefetchQuery(clientId),
  });
};

const handleUntag = (dataEntriesBeingBulkEdited, clientId) => {
  if (dataEntriesBeingBulkEdited.length === 0) {
    // At least one data entry must be changing to bother submitting
    return;
  }

  const updateDataEntriesInput = {
    isTagged1099: false,
  };

  const journalEntryIds = dataEntriesBeingBulkEdited.map(entry => entry.journalEntry.id);
  openModal(closeModal => (
    <ApplyConfirmationModal
      title="Submission confirmation"
      description={`You are about to submit changes to ${dataEntriesBeingBulkEdited.length} transactions that will now be removed from the 1099 report. Is this what you intended to do?`}
      closeModal={closeModal}
      onConfirm={() => {
        untag1099Transaction({
          dataEntriesBeingBulkEdited,
          updateDataEntriesInput,
          clientId,
          variables: {
            journalEntryIds,
          },
        });
      }}
    />
  ));
};

const handleSubmit = (submitEvent, bulkEditDataEntries, dataEntriesBeingBulkEdited, clientId) => {
  submitEvent.preventDefault();

  if (dataEntriesBeingBulkEdited.length === 0) {
    // At least one data entry must be changing to bother submitting
    return;
  }
  const { addOnId, editType, internalNoteBody, ledgerAccountId, vendorName, contractor, source } =
    getFormElementValues(submitEvent.target);
  const contractorValue = contractor === '' ? 'Unknown' : contractor;
  const sourceValue = source === '' ? 'Unknown' : source;

  if (
    !addOnId &&
    !internalNoteBody &&
    !ledgerAccountId &&
    !vendorName &&
    !contractorValue &&
    !sourceValue
  ) {
    // At least one property must be changing to bother submitting
    return;
  }
  const dataEntryIds = dataEntriesBeingBulkEdited.map(({ id }) => id);
  const updateDataEntriesInput = {
    clientId,
    dataEntryIds,
    addOnId: addOnId || null,
    internalNoteBody: internalNoteBody || null,
    ledgerAccountId: ledgerAccountId || null,
    [editType]: vendorName.trim() || null,
    contractor,
    source,
    isTagged1099: true,
  };
  const journalEntryIds = dataEntriesBeingBulkEdited.map(entry => entry.journalEntry.id);
  let showSuccessToast = true;
  if ((contractor !== undefined || contractor !== '') && source !== undefined) {
    showSuccessToast = false;
    openModal(closeModal => (
      <ApplyConfirmationModal
        title="Submission confirmation"
        description={`You are about to submit ${dataEntriesBeingBulkEdited.length} transactions that are now tagged as 1099, this will be visible for your client. Are you sure this is what you want to do?`}
        closeModal={closeModal}
        onConfirm={async () => {
          await tag1099Transaction({
            dataEntriesBeingBulkEdited,
            updateDataEntriesInput,
            clientId,
            variables: {
              input: {
                clientId: parseInt(clientId),
                source1099: source === '' ? null : source,
                contractorId: contractor === '' ? null : parseInt(contractor),
                journalEntryIds,
              },
            },
          });
        }}
      />
    ));
  }

  bulkEditDataEntries({
    // The cache is manually updated because the mutation response doesn't include enough
    // data for Apollo to update the cache automatically.
    update: (cache: any) =>
      updateCacheAfterBulkEdit(cache, dataEntriesBeingBulkEdited, updateDataEntriesInput),
    variables: {
      updateDataEntriesInput: {
        clientId,
        dataEntryIds,
        addOnId: addOnId || null,
        internalNoteBody: internalNoteBody || null,
        ledgerAccountId: ledgerAccountId || null,
        [editType]: vendorName.trim() || null,
      },
    }, // Get rid of contractor and source here
  }).then(() => {
    // Track how many journal entries were edited
    if (window.heap) {
      const numJournalEntriesEdited = dataEntriesBeingBulkEdited
        // Get the journal entry IDs of all the affected data entries
        .map(dataEntry => dataEntry.journalEntry.id)
        // Keep only the first (distinct) journal entry ID and take the length
        .filter((id, index, list) => list.indexOf(id) === index).length;
      let numFieldsChanged = 0;
      if (updateDataEntriesInput.addOnId !== null) {
        numFieldsChanged += 1;
      }
      if (updateDataEntriesInput.internalNoteBody !== null) {
        numFieldsChanged += 1;
      }
      if (updateDataEntriesInput.ledgerAccountId !== null) {
        numFieldsChanged += 1;
      }
      if (updateDataEntriesInput[editType] !== null) {
        numFieldsChanged += 1;
      }
      const numJournalEntryEditsInBatch = numJournalEntriesEdited * numFieldsChanged;
      window.heap.track('Product | BWA | Bulk Edit GL', {
        numJournalEntriesEdited,
        numJournalEntryEditsInBatch,
      });
      if (showSuccessToast) {
        showBulkEditCompletedToast(dataEntriesBeingBulkEdited.length);
      }
    }
  });
};

const showBulkEditCompletedToast = numChanged =>
  toastSuccess(
    'Bulk edit completed successfully',
    `${numChanged} ${numChanged === 1 ? 'entry was' : 'entries were'} successfully edited`,
    { position: 'top-right' },
  );

const showBulkEditErrorToast = error =>
  toastError(
    'Failed to bulk edit',
    `There was a problem bulk editing. The error was: ${error.message}`,
    { position: 'top-right' },
  );

type BulkEditFormContainerProps = {
  availableDataEntries: Array<DataEntry>;
  availableDataEntriesMap: Map<string, DataEntry>;
  shouldClearSelectionAfterSubmit?: boolean;
  isLightningGL?: boolean;
  dateRange: DateRange;
  filters: GeneralLedgerFilters;
  client: Client;
  dismissSideBar?: () => void;
};

const BulkEditFormContainer = ({
  availableDataEntries,
  availableDataEntriesMap,
  shouldClearSelectionAfterSubmit,
  isLightningGL,
  dateRange,
  filters,
  client,
  dismissSideBar,
}: BulkEditFormContainerProps) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isConfirmCategorizationTasksEnabled =
    features[SplitFeatureFlags.CONFIRM_CATEGORIZATION_TASKS]?.value === FeatureValue.FEATURE_ON;
  return (
    <ClientIdContext.Consumer>
      {clientId => (
        <PersonIdContext.Consumer>
          {personId => (
            <SelectedDataEntriesConsumer>
              {({ clearSelectedDataEntryIds, selectedDataEntryIds }: any) => {
                return (
                  <Mutation
                    mutation={BULK_EDIT_DATA_ENTRIES}
                    refetchQueries={
                      isLightningGL
                        ? [
                            {
                              query: GET_BALANCES_DATA,
                              variables: {
                                clientId,
                                fromDate: dateRange.startDate,
                                toDate: dateRange.endDate,
                                filterZeroEntryLedgers: true,
                              },
                            },
                          ]
                        : []
                    }
                    onCompleted={({ updateDataEntries }) => {
                      if (shouldClearSelectionAfterSubmit) {
                        clearSelectedDataEntryIds();

                        if (formRef.current) {
                          formRef.current.reset();
                        }
                      }
                    }}
                    onError={showBulkEditErrorToast}
                  >
                    {(bulkEditDataEntries, { loading }) => (
                      <Query query={GET_BULK_EDIT_FORM_DATA} variables={{ clientId }}>
                        {({ data }: any) => {
                          const addOnOptions =
                            data && data.addOns ? mapAddOnsToOptions(data.addOns) : [];
                          const ledgerAccountOptions =
                            data && data.ledgerAccounts
                              ? mapDataWithIdAndNameToOptions(
                                  // Filter out P/B holding ledgers
                                  data.ledgerAccounts.filter(ledger => ledger.category.id !== '9'),
                                )
                              : [];
                          let dataEntriesBeingBulkEdited;
                          if (isLightningGL) {
                            dataEntriesBeingBulkEdited = selectedDataEntryIds.map(id =>
                              availableDataEntriesMap.get(id),
                            );
                          } else {
                            dataEntriesBeingBulkEdited = availableDataEntries.filter(dataEntry =>
                              selectedDataEntryIds.includes(dataEntry.id),
                            );
                          }
                          return (
                            <BulkEditForm
                              addOnOptions={addOnOptions}
                              dataEntriesCount={dataEntriesBeingBulkEdited.length}
                              // @ts-expect-error - LegacyRef + useRef. Update when BulkEditForm is a functional component.
                              ref={formRef}
                              isSubmitting={loading}
                              ledgerAccountOptions={ledgerAccountOptions}
                              client={client}
                              onReset={clearSelectedDataEntryIds}
                              onUntag={() => handleUntag(dataEntriesBeingBulkEdited, clientId)}
                              dataEntries={dataEntriesBeingBulkEdited}
                              clientId={clientId}
                              personId={personId}
                              isConfirmCategorizationTasksEnabled={
                                isConfirmCategorizationTasksEnabled
                              }
                              onTaskOperationComplete={() => {
                                // Refresh data or perform any cleanup after task operations
                                clearSelectedDataEntryIds();
                              }}
                              onSubmit={submitEvent => {
                                handleSubmit(
                                  submitEvent,
                                  bulkEditDataEntries,
                                  dataEntriesBeingBulkEdited,
                                  clientId,
                                );
                              }}
                              onClick={dismissSideBar}
                            />
                          );
                        }}
                      </Query>
                    )}
                  </Mutation>
                );
              }}
            </SelectedDataEntriesConsumer>
          )}
        </PersonIdContext.Consumer>
      )}
    </ClientIdContext.Consumer>
  );
};

BulkEditFormContainer.defaultProps = {
  shouldClearSelectionAfterSubmit: true,
};

export default BulkEditFormContainer;
